import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlarmClock,
  AlertTriangle,
  ClipboardList,
  ListChecks,
  Lock,
  Play,
  Shield,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import {
  formatDuration,
  ModeChip,
  RunStatusChip,
  ScopeChip,
  type AttemptStart,
  type RunDetail,
} from '../components/assessments';

export const Route = createFileRoute('/_authed/assessments/runs/$runId/start')({
  component: AttemptStartPage,
});

interface TemplateMinimal {
  id: string;
  scope: import('../components/assessments').AssessmentScope;
  mode: import('../components/assessments').AssessmentMode;
  title: string;
  description: string | null;
  durationSeconds: number | null;
  questionDrawCount: number;
  passThresholdPercent: number | null;
  maxAttemptsPerUser: number;
  antiCheatProfile: string | null;
}

function AttemptStartPage() {
  const { runId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const runQuery = useQuery({
    queryKey: ['assessments', 'run', runId] as const,
    queryFn: ({ signal }) =>
      apiRequest<RunDetail>(
        `/api/assessments/runs/${encodeURIComponent(runId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      if (err instanceof ApiError && err.status === 403) return false;
      return failureCount < 2;
    },
  });

  const templateId = runQuery.data?.templateId;

  // Taker users may not have GET access to the template — but we surface
  // its scope/duration via the run object's stats. For the start screen we
  // still ask the server for the template via the public template GET. If
  // it 403s, we degrade gracefully.
  const templateQuery = useQuery({
    queryKey: ['assessments', 'template', templateId ?? ''] as const,
    enabled: Boolean(templateId),
    queryFn: ({ signal }) =>
      apiRequest<TemplateMinimal>(
        `/api/assessments/templates/${encodeURIComponent(templateId!)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const startMutation = useMutation({
    mutationFn: async () =>
      apiRequest<AttemptStart>(
        `/api/assessments/runs/${encodeURIComponent(runId)}/start`,
        { method: 'POST', body: {} },
      ),
    onSuccess: (data) => {
      void navigate({
        to: '/assessments/attempts/$attemptId',
        params: { attemptId: data.attemptId },
      });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError ? err.message : 'Could not start the attempt.';
      toast(message, { variant: 'error' });
    },
  });

  return (
    <ListShell
      title={runQuery.data?.title ?? 'Start the test'}
      description="Read the details below carefully before you begin."
    >
      <GatedSection
        error={runQuery.error}
        title="Test"
        onRetry={() => runQuery.refetch()}
      >
        {runQuery.isLoading || !runQuery.data ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <StartPanel
            run={runQuery.data}
            template={templateQuery.data}
            isStarting={startMutation.isPending}
            onStart={() => startMutation.mutate()}
          />
        )}
      </GatedSection>
    </ListShell>
  );
}

interface StartPanelProps {
  run: RunDetail;
  template: TemplateMinimal | undefined;
  isStarting: boolean;
  onStart: () => void;
}

function StartPanel({ run, template, isStarting, onStart }: StartPanelProps) {
  const strict = template?.antiCheatProfile === 'hiring_strict';
  const showRules = strict || template?.mode === 'timed_exam';

  const closed = run.status === 'closed' || run.status === 'cancelled';
  const notYetOpen = run.opensAt ? Date.parse(run.opensAt) > Date.now() : false;
  const disabled = closed || notYetOpen || isStarting;

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <div className="space-y-3 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {template ? <ScopeChip scope={template.scope} /> : null}
          {template ? <ModeChip mode={template.mode} /> : null}
          <RunStatusChip status={run.status} />
        </div>
        <h2 className="font-display text-2xl font-semibold leading-snug">
          {run.title}
        </h2>
        {template?.description ? (
          <p className="text-sm text-[color:var(--color-text)]">
            {template.description}
          </p>
        ) : null}

        <dl className="mt-2 grid gap-3 sm:grid-cols-2">
          <Stat
            icon={<ListChecks size={16} aria-hidden="true" />}
            label="Questions"
            value={template ? `${template.questionDrawCount}` : '—'}
          />
          <Stat
            icon={<AlarmClock size={16} aria-hidden="true" />}
            label="Duration"
            value={
              template?.mode === 'timed_exam'
                ? formatDuration(template.durationSeconds)
                : 'No timer'
            }
          />
          {template?.passThresholdPercent !== undefined &&
          template?.passThresholdPercent !== null ? (
            <Stat
              icon={<ClipboardList size={16} aria-hidden="true" />}
              label="Pass threshold"
              value={`${template.passThresholdPercent}%`}
            />
          ) : null}
          <Stat
            icon={<Shield size={16} aria-hidden="true" />}
            label="Attempts allowed"
            value={`${template?.maxAttemptsPerUser ?? '—'}`}
          />
        </dl>
      </div>

      {showRules ? (
        <div className="space-y-2 rounded-2xl border border-gold-500/40 bg-gold-50/60 p-5 dark:bg-gold-700/15">
          <header className="flex items-center gap-2">
            <AlertTriangle
              size={16}
              aria-hidden="true"
              className="text-gold-700"
            />
            <h3 className="font-display text-base font-semibold text-gold-900 dark:text-gold-50">
              Integrity expectations
            </h3>
          </header>
          <ul className="ml-6 list-disc space-y-1 text-sm text-[color:var(--color-text)]">
            {template?.mode === 'timed_exam' ? (
              <li>
                The timer starts when you click <strong>Begin attempt</strong>{' '}
                and cannot be paused.
              </li>
            ) : null}
            <li>Do not refresh the page — answers save automatically.</li>
            {strict ? (
              <>
                <li>
                  Stay focused on this browser tab. Switching tabs is recorded
                  and the academy reviews flagged attempts.
                </li>
                <li>
                  You may only attempt this test once. Closing the tab
                  does not give you a second try.
                </li>
                <li>
                  No external help. The academy verifies results during the
                  interview.
                </li>
              </>
            ) : null}
            <li>
              When time runs out, the test auto-submits with the answers
              you have saved.
            </li>
          </ul>
        </div>
      ) : null}

      {closed ? (
        <div className="flex items-start gap-2 rounded-md border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 px-4 py-3 text-sm text-[color:var(--color-danger)]">
          <Lock size={14} aria-hidden="true" className="mt-0.5" />
          <span>
            This test is {run.status}. If you believe this is a mistake,
            contact the academy office.
          </span>
        </div>
      ) : notYetOpen ? (
        <div className="flex items-start gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-3 text-sm text-[color:var(--color-text-muted)]">
          <AlarmClock size={14} aria-hidden="true" className="mt-0.5" />
          <span>
            This test opens on{' '}
            <strong>
              {new Date(run.opensAt!).toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </strong>
            . Please come back then.
          </span>
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-3">
        <Link
          to="/me/assessments"
          className="text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          Back to my tests & quizzes
        </Link>
        <Button
          size="lg"
          icon={<Play size={18} aria-hidden="true" />}
          onClick={onStart}
          loading={isStarting}
          disabled={disabled}
        >
          Begin attempt
        </Button>
      </div>
    </section>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {icon}
        {label}
      </dt>
      <dd className="mt-0.5 font-display text-lg font-bold text-[color:var(--color-text)]">
        {value}
      </dd>
    </div>
  );
}
